// ============================================
// EXPENSE MANAGEMENT - COMPREHENSIVE DATA MODEL
// ============================================

// Expense Categories - Extended for comprehensive tracking
export enum ExpenseCategory {
  // Travel & Transportation
  travel = 'travel',
  airfare = 'airfare',
  lodging = 'lodging',
  carRental = 'car_rental',
  mileage = 'mileage',
  parking = 'parking',
  tolls = 'tolls',
  rideshare = 'rideshare',
  publicTransit = 'public_transit',
  
  // Food & Entertainment
  meals = 'meals',
  clientMeals = 'client_meals',
  teamMeals = 'team_meals',
  entertainment = 'entertainment',
  
  // Office & Equipment
  office = 'office',
  officeSupplies = 'office_supplies',
  equipment = 'equipment',
  furniture = 'furniture',
  computerHardware = 'computer_hardware',
  
  // Technology & Software
  software = 'software',
  saasSubscriptions = 'saas_subscriptions',
  cloudServices = 'cloud_services',
  telecommunications = 'telecommunications',
  internet = 'internet',
  
  // Professional Services
  professional = 'professional',
  legal = 'legal',
  accounting = 'accounting',
  consulting = 'consulting',
  contractors = 'contractors',
  
  // Facilities & Operations
  rent = 'rent',
  utilities = 'utilities',
  maintenance = 'maintenance',
  cleaning = 'cleaning',
  security = 'security',
  
  // Marketing & Sales
  marketing = 'marketing',
  advertising = 'advertising',
  eventSponsorship = 'event_sponsorship',
  tradeShows = 'trade_shows',
  promotionalMaterials = 'promotional_materials',
  
  // HR & Training
  training = 'training',
  conferences = 'conferences',
  certifications = 'certifications',
  recruitment = 'recruitment',
  employeeBenefits = 'employee_benefits',
  
  // Insurance & Compliance
  insurance = 'insurance',
  licenses = 'licenses',
  permits = 'permits',
  compliance = 'compliance',
  
  // Miscellaneous
  shipping = 'shipping',
  subscriptions = 'subscriptions',
  donations = 'donations',
  bankFees = 'bank_fees',
  taxes = 'taxes',
  other = 'other',
}

// Category display names for UI
export const ExpenseCategoryLabels: Record<ExpenseCategory, string> = {
  [ExpenseCategory.travel]: 'Travel',
  [ExpenseCategory.airfare]: 'Airfare',
  [ExpenseCategory.lodging]: 'Lodging/Hotel',
  [ExpenseCategory.carRental]: 'Car Rental',
  [ExpenseCategory.mileage]: 'Mileage Reimbursement',
  [ExpenseCategory.parking]: 'Parking',
  [ExpenseCategory.tolls]: 'Tolls',
  [ExpenseCategory.rideshare]: 'Rideshare (Uber/Lyft)',
  [ExpenseCategory.publicTransit]: 'Public Transit',
  [ExpenseCategory.meals]: 'Meals',
  [ExpenseCategory.clientMeals]: 'Client Meals',
  [ExpenseCategory.teamMeals]: 'Team Meals',
  [ExpenseCategory.entertainment]: 'Entertainment',
  [ExpenseCategory.office]: 'Office',
  [ExpenseCategory.officeSupplies]: 'Office Supplies',
  [ExpenseCategory.equipment]: 'Equipment',
  [ExpenseCategory.furniture]: 'Furniture',
  [ExpenseCategory.computerHardware]: 'Computer Hardware',
  [ExpenseCategory.software]: 'Software',
  [ExpenseCategory.saasSubscriptions]: 'SaaS Subscriptions',
  [ExpenseCategory.cloudServices]: 'Cloud Services',
  [ExpenseCategory.telecommunications]: 'Telecommunications',
  [ExpenseCategory.internet]: 'Internet',
  [ExpenseCategory.professional]: 'Professional Services',
  [ExpenseCategory.legal]: 'Legal',
  [ExpenseCategory.accounting]: 'Accounting',
  [ExpenseCategory.consulting]: 'Consulting',
  [ExpenseCategory.contractors]: 'Contractors',
  [ExpenseCategory.rent]: 'Rent',
  [ExpenseCategory.utilities]: 'Utilities',
  [ExpenseCategory.maintenance]: 'Maintenance',
  [ExpenseCategory.cleaning]: 'Cleaning',
  [ExpenseCategory.security]: 'Security',
  [ExpenseCategory.marketing]: 'Marketing',
  [ExpenseCategory.advertising]: 'Advertising',
  [ExpenseCategory.eventSponsorship]: 'Event Sponsorship',
  [ExpenseCategory.tradeShows]: 'Trade Shows',
  [ExpenseCategory.promotionalMaterials]: 'Promotional Materials',
  [ExpenseCategory.training]: 'Training',
  [ExpenseCategory.conferences]: 'Conferences',
  [ExpenseCategory.certifications]: 'Certifications',
  [ExpenseCategory.recruitment]: 'Recruitment',
  [ExpenseCategory.employeeBenefits]: 'Employee Benefits',
  [ExpenseCategory.insurance]: 'Insurance',
  [ExpenseCategory.licenses]: 'Licenses',
  [ExpenseCategory.permits]: 'Permits',
  [ExpenseCategory.compliance]: 'Compliance',
  [ExpenseCategory.shipping]: 'Shipping',
  [ExpenseCategory.subscriptions]: 'Subscriptions',
  [ExpenseCategory.donations]: 'Donations',
  [ExpenseCategory.bankFees]: 'Bank Fees',
  [ExpenseCategory.taxes]: 'Taxes',
  [ExpenseCategory.other]: 'Other',
};

// Category groups for UI organization
export const ExpenseCategoryGroups: Record<string, ExpenseCategory[]> = {
  'Travel & Transportation': [
    ExpenseCategory.travel,
    ExpenseCategory.airfare,
    ExpenseCategory.lodging,
    ExpenseCategory.carRental,
    ExpenseCategory.mileage,
    ExpenseCategory.parking,
    ExpenseCategory.tolls,
    ExpenseCategory.rideshare,
    ExpenseCategory.publicTransit,
  ],
  'Food & Entertainment': [
    ExpenseCategory.meals,
    ExpenseCategory.clientMeals,
    ExpenseCategory.teamMeals,
    ExpenseCategory.entertainment,
  ],
  'Office & Equipment': [
    ExpenseCategory.office,
    ExpenseCategory.officeSupplies,
    ExpenseCategory.equipment,
    ExpenseCategory.furniture,
    ExpenseCategory.computerHardware,
  ],
  'Technology & Software': [
    ExpenseCategory.software,
    ExpenseCategory.saasSubscriptions,
    ExpenseCategory.cloudServices,
    ExpenseCategory.telecommunications,
    ExpenseCategory.internet,
  ],
  'Professional Services': [
    ExpenseCategory.professional,
    ExpenseCategory.legal,
    ExpenseCategory.accounting,
    ExpenseCategory.consulting,
    ExpenseCategory.contractors,
  ],
  'Facilities & Operations': [
    ExpenseCategory.rent,
    ExpenseCategory.utilities,
    ExpenseCategory.maintenance,
    ExpenseCategory.cleaning,
    ExpenseCategory.security,
  ],
  'Marketing & Sales': [
    ExpenseCategory.marketing,
    ExpenseCategory.advertising,
    ExpenseCategory.eventSponsorship,
    ExpenseCategory.tradeShows,
    ExpenseCategory.promotionalMaterials,
  ],
  'HR & Training': [
    ExpenseCategory.training,
    ExpenseCategory.conferences,
    ExpenseCategory.certifications,
    ExpenseCategory.recruitment,
    ExpenseCategory.employeeBenefits,
  ],
  'Insurance & Compliance': [
    ExpenseCategory.insurance,
    ExpenseCategory.licenses,
    ExpenseCategory.permits,
    ExpenseCategory.compliance,
  ],
  'Miscellaneous': [
    ExpenseCategory.shipping,
    ExpenseCategory.subscriptions,
    ExpenseCategory.donations,
    ExpenseCategory.bankFees,
    ExpenseCategory.taxes,
    ExpenseCategory.other,
  ],
};

export enum ExpenseStatus {
  draft = 'draft',
  submitted = 'submitted',
  pendingApproval = 'pending_approval',
  approved = 'approved',
  rejected = 'rejected',
  pendingPayment = 'pending_payment',
  paid = 'paid',
  cancelled = 'cancelled',
}

export const ExpenseStatusLabels: Record<ExpenseStatus, string> = {
  [ExpenseStatus.draft]: 'Draft',
  [ExpenseStatus.submitted]: 'Submitted',
  [ExpenseStatus.pendingApproval]: 'Pending Approval',
  [ExpenseStatus.approved]: 'Approved',
  [ExpenseStatus.rejected]: 'Rejected',
  [ExpenseStatus.pendingPayment]: 'Pending Payment',
  [ExpenseStatus.paid]: 'Paid',
  [ExpenseStatus.cancelled]: 'Cancelled',
};

export enum PaymentMethod {
  cash = 'cash',
  companyCard = 'company_card',
  personalCard = 'personal_card',
  bankTransfer = 'bank_transfer',
  check = 'check',
  invoice = 'invoice',
  pettyCash = 'petty_cash',
  other = 'other',
}

export const PaymentMethodLabels: Record<PaymentMethod, string> = {
  [PaymentMethod.cash]: 'Cash',
  [PaymentMethod.companyCard]: 'Company Card',
  [PaymentMethod.personalCard]: 'Personal Card',
  [PaymentMethod.bankTransfer]: 'Bank Transfer',
  [PaymentMethod.check]: 'Check',
  [PaymentMethod.invoice]: 'Invoice',
  [PaymentMethod.pettyCash]: 'Petty Cash',
  [PaymentMethod.other]: 'Other',
};

export enum RecurrenceFrequency {
  oneTime = 'one_time',
  weekly = 'weekly',
  biWeekly = 'bi_weekly',
  monthly = 'monthly',
  quarterly = 'quarterly',
  annually = 'annually',
}

export const RecurrenceFrequencyLabels: Record<RecurrenceFrequency, string> = {
  [RecurrenceFrequency.oneTime]: 'One Time',
  [RecurrenceFrequency.weekly]: 'Weekly',
  [RecurrenceFrequency.biWeekly]: 'Bi-Weekly',
  [RecurrenceFrequency.monthly]: 'Monthly',
  [RecurrenceFrequency.quarterly]: 'Quarterly',
  [RecurrenceFrequency.annually]: 'Annually',
};

// Mileage tracking for travel expenses
export interface MileageDetails {
  startLocation: string;
  endLocation: string;
  miles: number;
  ratePerMile: number; // Current IRS rate or custom rate
  roundTrip: boolean;
  purpose: string;
}

// Receipt/Attachment information
export interface ExpenseAttachment {
  id: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  uploadedAt: Date;
  uploadedBy: string;
  ocrData?: Record<string, unknown>; // OCR extracted data if applicable
}

// Approval workflow step
export interface ApprovalStep {
  stepNumber: number;
  approverRole: string;
  approverId?: string;
  approverName?: string;
  approverEmail?: string;
  status: 'pending' | 'approved' | 'rejected' | 'skipped';
  actionDate?: Date;
  comments?: string;
  thresholdAmount?: number; // Approval required if expense exceeds this amount
}

// Main Expense Interface - Comprehensive
export interface Expense {
  id: string;
  
  // Employee Information
  employeeId: string;
  employeeName: string;
  employeeEmail?: string;
  employeeDepartment?: string;
  
  // Report association
  reportId?: string;
  reportNumber?: string;
  
  // Core Expense Details
  category: ExpenseCategory;
  subcategory?: string;
  description: string;
  amount: number;
  currency: string;
  exchangeRate?: number; // For foreign currency expenses
  originalAmount?: number; // Amount in original currency
  originalCurrency?: string;
  
  // Tax information
  taxAmount?: number;
  taxRate?: number;
  taxDeductible: boolean;
  
  // Date Information
  expenseDate: Date;
  submittedAt?: Date;
  
  // Payment Information
  paymentMethod: PaymentMethod;
  cardLastFour?: string; // Last 4 digits if card payment
  
  // Merchant/Vendor Details
  merchant?: string;
  merchantCategory?: string;
  vendorId?: string;
  vendorName?: string;
  location?: string;
  city?: string;
  state?: string;
  country?: string;
  
  // Receipts & Attachments
  attachments: ExpenseAttachment[];
  receiptUrl?: string; // Primary receipt (backward compatibility)
  receiptFileName?: string;
  receiptRequired: boolean;
  receiptVerified: boolean;
  
  // Mileage Tracking (for mileage expenses)
  mileageDetails?: MileageDetails;
  
  // Project/Cost Tracking
  projectId?: string;
  projectName?: string;
  projectCode?: string;
  departmentId?: string;
  departmentName?: string;
  costCenterId?: string;
  costCenterName?: string;
  glAccountCode?: string;
  
  // Billable Tracking
  billable: boolean;
  clientId?: string;
  clientName?: string;
  billedToClient: boolean;
  billedDate?: Date;
  invoiceNumber?: string;
  
  // Recurring Expense
  isRecurring: boolean;
  recurrence?: {
    frequency: RecurrenceFrequency;
    startDate: Date;
    endDate?: Date;
    nextOccurrence?: Date;
    parentExpenseId?: string; // Reference to original recurring expense
  };
  
  // Status & Workflow
  status: ExpenseStatus;
  
  // Approval Workflow
  approvalWorkflow: ApprovalStep[];
  currentApprovalStep?: number;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
  rejectedBy?: string;
  rejectedByName?: string;
  rejectedAt?: Date;
  rejectionReason?: string;
  
  // Payment Processing
  paidAt?: Date;
  paidBy?: string;
  paymentReference?: string;
  reimbursementMethod?: string;
  
  // Policy & Compliance
  policyViolations: PolicyViolation[];
  requiresAudit: boolean;
  auditedBy?: string;
  auditedAt?: Date;
  auditNotes?: string;
  
  // Additional Information
  notes?: string;
  internalNotes?: string; // Admin-only notes
  tags: string[];
  customFields?: Record<string, unknown>;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  lastModifiedBy?: string;
}

// Policy Violation for compliance tracking
export interface PolicyViolation {
  ruleId: string;
  ruleName: string;
  violationType: 'warning' | 'error' | 'info';
  message: string;
  suggestedAction?: string;
  overrideApproved?: boolean;
  overrideApprovedBy?: string;
  overrideApprovedAt?: Date;
  overrideReason?: string;
}

// Expense Report - For grouping multiple expenses
export interface ExpenseReport {
  id: string;
  reportNumber: string;
  title: string;
  description?: string;
  
  // Employee Information
  employeeId: string;
  employeeName: string;
  employeeEmail?: string;
  employeeDepartment?: string;
  
  // Report Period
  startDate: Date;
  endDate: Date;
  
  // Status
  status: ExpenseStatus;
  
  // Expenses
  expenseIds: string[]; // References to expense documents
  expenses?: Expense[]; // Populated expenses (not stored in DB)
  
  // Financial Summary
  subtotal: number;
  taxTotal: number;
  total: number;
  currency: string;
  
  // Category Breakdown
  categoryBreakdown: Record<string, number>;
  
  // Reimbursement Details
  reimbursableAmount: number; // Amount to be reimbursed to employee
  nonReimbursableAmount: number; // Company card, etc.
  billableAmount: number; // Amount billable to clients
  
  // Approval Workflow
  approvalWorkflow: ApprovalStep[];
  currentApprovalStep?: number;
  
  // Submission & Approval
  submittedAt?: Date;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
  rejectedBy?: string;
  rejectedAt?: Date;
  rejectionReason?: string;
  
  // Payment
  paidAt?: Date;
  paidBy?: string;
  paymentReference?: string;
  paymentMethod?: string;
  
  // Notes
  notes?: string;
  internalNotes?: string;
  tags: string[];
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
}

// Expense Policy - Defines spending rules and limits
export interface ExpensePolicy {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  priority: number; // Higher number = higher priority
  
  // Scope - Who this policy applies to
  scope: {
    allEmployees: boolean;
    departments?: string[];
    roles?: string[];
    employeeIds?: string[];
    excludedEmployeeIds?: string[];
  };
  
  // Category Limits
  categoryLimits: {
    category: ExpenseCategory;
    dailyLimit?: number;
    singleExpenseLimit?: number;
    monthlyLimit?: number;
    annualLimit?: number;
    requiresReceipt: boolean;
    requiresApproval: boolean;
    approvalThreshold?: number; // Approval required above this amount
    notes?: string;
  }[];
  
  // General Limits
  generalLimits: {
    singleExpenseLimit?: number;
    dailyLimit?: number;
    weeklyLimit?: number;
    monthlyLimit?: number;
    quarterlyLimit?: number;
    annualLimit?: number;
  };
  
  // Approval Thresholds
  approvalThresholds: {
    thresholdAmount: number;
    approverRole: string;
    requiresMultipleApprovers: boolean;
  }[];
  
  // Receipt Requirements
  receiptRequirements: {
    minimumAmount: number; // Receipt required for expenses >= this amount
    requiredCategories: ExpenseCategory[]; // Categories that always require receipts
    exemptCategories: ExpenseCategory[]; // Categories exempt from receipt requirement
  };
  
  // Allowed Expense Types
  restrictions: {
    allowedCategories?: ExpenseCategory[];
    blockedCategories?: ExpenseCategory[];
    allowedVendors?: string[];
    blockedVendors?: string[];
    allowedPaymentMethods?: PaymentMethod[];
    blockedPaymentMethods?: PaymentMethod[];
  };
  
  // Mileage Rate
  mileageRate: number; // Rate per mile for mileage reimbursement
  
  // Per Diem Rates (by expense type)
  perDiemRates?: {
    meals?: number;
    lodging?: number;
    incidentals?: number;
  };
  
  // Metadata
  effectiveDate: Date;
  expirationDate?: Date;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  lastModifiedBy?: string;
}

// Budget - For tracking departmental/project spending
export interface ExpenseBudget {
  id: string;
  name: string;
  description?: string;
  
  // Budget Period
  fiscalYear: number;
  periodType: 'monthly' | 'quarterly' | 'annual';
  startDate: Date;
  endDate: Date;
  
  // Budget Scope
  scope: {
    type: 'organization' | 'department' | 'project' | 'cost_center';
    departmentId?: string;
    departmentName?: string;
    projectId?: string;
    projectName?: string;
    costCenterId?: string;
    costCenterName?: string;
  };
  
  // Budget Amounts
  totalBudget: number;
  categoryBudgets: {
    category: ExpenseCategory;
    budgetAmount: number;
    spent: number;
    remaining: number;
    percentUsed: number;
  }[];
  
  // Spending Tracking (calculated)
  totalSpent: number;
  totalCommitted: number; // Approved but not yet paid
  totalRemaining: number;
  percentUsed: number;
  
  // Alerts
  alertThresholds: {
    warningPercent: number; // e.g., 75%
    criticalPercent: number; // e.g., 90%
  };
  
  // Status
  isActive: boolean;
  isFrozen: boolean; // No new expenses allowed
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
}

// Expense Analytics - Summary statistics
export interface ExpenseAnalytics {
  period: {
    startDate: Date;
    endDate: Date;
    periodType: 'day' | 'week' | 'month' | 'quarter' | 'year';
  };
  
  // Summary Metrics
  totalExpenses: number;
  totalAmount: number;
  averageExpenseAmount: number;
  medianExpenseAmount: number;
  
  // By Status
  byStatus: Record<ExpenseStatus, { count: number; amount: number }>;
  
  // By Category
  byCategory: {
    category: ExpenseCategory;
    count: number;
    amount: number;
    percentOfTotal: number;
    avgAmount: number;
  }[];
  
  // By Department
  byDepartment: {
    departmentId: string;
    departmentName: string;
    count: number;
    amount: number;
    budgetAmount?: number;
    percentOfBudget?: number;
  }[];
  
  // By Employee (Top Spenders)
  byEmployee: {
    employeeId: string;
    employeeName: string;
    count: number;
    amount: number;
    avgAmount: number;
  }[];
  
  // By Project
  byProject: {
    projectId: string;
    projectName: string;
    count: number;
    amount: number;
    billableAmount: number;
  }[];
  
  // Trends
  trend: {
    date: Date;
    count: number;
    amount: number;
  }[];
  
  // Approval Metrics
  approvalMetrics: {
    avgApprovalTime: number; // In hours
    pendingCount: number;
    pendingAmount: number;
    rejectionRate: number;
  };
  
  // Policy Compliance
  policyCompliance: {
    totalViolations: number;
    violationsByType: Record<string, number>;
    complianceRate: number;
  };
  
  // Comparison to Previous Period
  comparison?: {
    previousPeriodAmount: number;
    percentChange: number;
    previousPeriodCount: number;
    countChange: number;
  };
}

// Filter for searching expenses
export interface ExpenseFilter {
  // Text search
  searchQuery?: string;
  
  // Date range
  startDate?: Date;
  endDate?: Date;
  
  // Status
  statuses?: ExpenseStatus[];
  
  // Categories
  categories?: ExpenseCategory[];
  
  // Amount range
  minAmount?: number;
  maxAmount?: number;
  
  // Employee
  employeeIds?: string[];
  employeeDepartments?: string[];
  
  // Project/Cost Center
  projectIds?: string[];
  departmentIds?: string[];
  costCenterIds?: string[];
  
  // Payment Method
  paymentMethods?: PaymentMethod[];
  
  // Billable
  billable?: boolean;
  billedToClient?: boolean;
  
  // Receipt status
  hasReceipt?: boolean;
  receiptVerified?: boolean;
  
  // Policy violations
  hasPolicyViolations?: boolean;
  
  // Recurring
  isRecurring?: boolean;
  
  // Report association
  reportId?: string;
  hasReport?: boolean;
  
  // Tags
  tags?: string[];
  
  // Vendor/Merchant
  vendorIds?: string[];
  merchantSearch?: string;
  
  // Audit
  requiresAudit?: boolean;
  isAudited?: boolean;
}

// Sort options
export interface ExpenseSortOptions {
  field: 'expenseDate' | 'amount' | 'createdAt' | 'submittedAt' | 'category' | 'status' | 'employeeName';
  direction: 'asc' | 'desc';
}

// Pagination
export interface PaginationOptions {
  page: number;
  pageSize: number;
}

// Paginated result
export interface PaginatedExpenses {
  expenses: Expense[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasMore: boolean;
}

// Bulk operation result
export interface BulkOperationResult {
  successCount: number;
  failureCount: number;
  errors: { id: string; error: string }[];
}

// Export options
export interface ExpenseExportOptions {
  format: 'csv' | 'xlsx' | 'pdf';
  includeAttachments: boolean;
  includeApprovalHistory: boolean;
  dateRange?: { startDate: Date; endDate: Date };
  filter?: ExpenseFilter;
}
